/**
 * Suche über alle Richtlinien des Mandanten: der Server wählt die Abschnitte, in denen alle
 * Begriffe vorkommen; Gewichtung und Ausschnitte wie in der Suche innerhalb einer Richtlinie. Ein
 * Klick öffnet den Abschnitt mit dem Suchbegriff, dort sind die Vorkommen markiert.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";

import { Input } from "../components/ui/input";
import { useGuidelinesAPI, type GlobalSearchSection } from "./GuidelinesAPI";
import { displayTitle, GUIDELINES_PATH } from "./guidelinesPath";
import { searchSections } from "./search";
import { SearchResults } from "./SearchResults";

export function GlobalSearch({ customer }: { customer: string }) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(customer);
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<{ query: string; sections: GlobalSearchSection[]; truncated: boolean } | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setFound(null);
      return;
    }
    const timer = setTimeout(() => {
      api
        .searchAll(q)
        .then((r) => setFound({ query: q, ...r }))
        .catch((e) => toast.error(e instanceof Error ? e.message : String(e)));
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, customer]);

  const groups = useMemo(() => {
    if (!found) return [];
    const byDoc = new Map<string, { title: string; sections: GlobalSearchSection[] }>();
    for (const s of found.sections) {
      const g = byDoc.get(s.documentId) ?? { title: s.documentTitle, sections: [] };
      g.sections.push(s);
      byDoc.set(s.documentId, g);
    }
    return Array.from(byDoc.entries())
      .map(([documentId, g]) => {
        const results = searchSections(g.sections, found.query, 20);
        return { documentId, title: g.title, results, score: results.reduce((n, r) => Math.max(n, r.score), 0), numbers: new Map<string, string>(g.sections.map((s): [string, string] => [s._id, s.number])) };
      })
      .filter((g) => g.results.length)
      .sort((a, b) => b.score - a.score);
  }, [found]);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-8" placeholder={t("guidelinesPage.globalSearch.placeholder")} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="global-search" />
      </div>
      {found && !groups.length ? <p className="text-sm text-muted-foreground">{t("guidelinesPage.search.none")}</p> : null}
      {groups.map((g) => (
        <div key={g.documentId} className="rounded-lg border bg-card" data-search-document={g.documentId}>
          <div className="border-b px-3 py-2 text-sm font-semibold">{displayTitle(g.title)}</div>
          <SearchResults
            results={g.results}
            selectedId={null}
            onSelect={(sectionId) => navigate(`${GUIDELINES_PATH}/${g.documentId}?section=${encodeURIComponent(g.numbers.get(sectionId) ?? "")}&q=${encodeURIComponent(found!.query)}`)}
          />
        </div>
      ))}
      {found?.truncated ? <p className="text-xs text-muted-foreground">{t("guidelinesPage.globalSearch.truncated")}</p> : null}
    </div>
  );
}
